import React, { useState, useEffect, useContext, useMemo } from "react";
import { FormGroup, Input, Row, Col } from "reactstrap";
import HusariaSelect from "components/HusariaSelect.js";
import Tooltip from "components/ToolTip.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
/**
 * UserStep — wizard step for user information.
 *
 * Props:
 *   formData         — the wizard's full formData
 *   updateSection    — (section, field, value) => void
 *   availableModules — array of module objects with { name }
 */
const Toggle = ({ checked, onChange, id: toggleId }) => (
  <label className="custom-toggle mb-0">
    <input
      type="checkbox"
      id={toggleId}
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span
      className="custom-toggle-slider rounded-circle"
      data-label-off="Off"
      data-label-on="On"
    />
  </label>
);

function UserStep({ formData = {}, updateSection, availableModules = [] }) {
  const user = formData.user || {};
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const handleChange = (e) => {
    const { id, value } = e.target;
    const field = id.replace("input-", "");
    updateSection("user", field, value);
  };

  const handleToggle = () => {
    updateSection("user", "new_user", !user.new_user);
  };

  const labelStyle = {
    color: isDark ? "#e2e8f0" : "#495057",
    fontWeight: 500,
  };

  return (
    <div className="pl-lg-4">
      {/* New User Toggle */}
      <Row className="mb-3">
        <Col lg="12">
          <FormGroup className="d-flex align-items-center gap-2 mb-0">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                paddingBottom: "0.25rem",
              }}
            >
              <span
                style={{
                  ...labelStyle,
                  fontWeight: 600,
                  minWidth: "90px",
                }}
              >
                Create new admin?
              </span>
              <Toggle
                checked={formData.admin_user.new_user === true}
                onChange={() => {
                  updateSection(
                    "admin_user",
                    "new_user",
                    !formData.admin_user.new_user,
                  );
                }}
                id={`cf-toggle-admin`}
              />
            </div>
          </FormGroup>
        </Col>
      </Row>

      {/* User fields — only shown when new_user is true */}
      {formData.admin_user.new_user && (
        <>
          <Row>
            <Col lg="6">
              <FormGroup>
                <div className="d-flex align-items-center mb-2">
                  <label
                    className="form-control-label"
                    htmlFor="input-first_name"
                  >
                    First Name <span className="text-danger">*</span>
                  </label>
                </div>
                <Input
                  id="input-first_name"
                  value={formData.admin_user.first_name || ""}
                  onChange={(e) => {
                    updateSection("admin_user", "first_name", e.target.value);
                  }}
                  placeholder="John"
                  type="text"
                  required
                />
              </FormGroup>
            </Col>
            <Col lg="6">
              <FormGroup>
                <div className="d-flex align-items-center mb-2">
                  <label className="form-control-label" htmlFor="input-last_name">
                    Last Name <span className="text-danger">*</span>
                  </label>
                </div>
                <Input
                  id="input-last_name"
                  value={formData.admin_user.last_name || ""}
                  onChange={(e) => {
                    updateSection("admin_user", "last_name", e.target.value);
                  }}
                  placeholder="Doe"
                  type="text"
                  required
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg="6">
              <FormGroup>
                <div className="d-flex align-items-center mb-2">
                  <label className="form-control-label" htmlFor="input-email">
                    Email <span className="text-danger">*</span>
                  </label>
                </div>
                <Input
                  id="input-email"
                  value={formData.admin_user.email || ""}
                  onChange={(e) => {
                    updateSection("admin_user", "email", e.target.value);
                  }}
                  placeholder="john.doe@example.com"
                  type="email"
                  required
                />
              </FormGroup>
            </Col>
            <Col lg="6">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-phone">
                  Phone
                </label>
                <Tooltip text="Will be used for SMS updates.">
                  <label
                    style={{
                      marginLeft: "10px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      paddingBottom: "5px",
                      cursor: "pointer",
                    }}
                  >
                    ?
                  </label>
                </Tooltip>
                <Input
                  id="input-phone"
                  value={formData.admin_user.phone || ""}
                  onChange={(e) => {
                    updateSection("admin_user", "phone", e.target.value);
                  }}
                  placeholder="+1 555 000 0000"
                  type="tel"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <FormGroup>
                <div className="d-flex align-items-center mb-2">
                  <label className="form-control-label" htmlFor="module_perms">
                    Modules
                  </label>
                  <Tooltip text="Leave empty for no access">
                    <label
                      style={{
                        marginLeft: "10px",
                        fontSize: "12px",
                        fontWeight: "bold",
                        paddingBottom: "5px",
                        cursor: "pointer",
                      }}
                    >
                      ?
                    </label>
                  </Tooltip>
                </div>
                <HusariaSelect
                  isMulti
                  name="module_perms"
                  id="module_perms"
                  options={formData.domain_detail.modules.map((m) => ({
                    value: m,
                    label: m,
                  }))}
                  value={(formData.admin_user.modules || [])
                    .map((name) => {
                      const module = formData.domain_detail.modules.find(
                        (m) => m === name,
                      );
                      return module ? { value: module, label: module } : null;
                    })
                    .filter(Boolean)}
                  onChange={(selectedOptions) => {
                    const selectedNames = selectedOptions.map(
                      (opt) => opt.value,
                    );
                    updateSection("admin_user", "modules", selectedNames);
                  }}
                  style={{ height: "46px" }}
                />
              </FormGroup>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}

export default UserStep;
